import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useUrlFilters } from '../../hooks/useUrlFilters'
import {
  BarChart3, Phone, PhoneOff, PhoneIncoming, PhoneOutgoing,
  Star, TrendingUp, TrendingDown,
  ChevronDown, ChevronRight, Shield, ArrowUpDown, HelpCircle,
} from 'lucide-react'
import { teamsAnalyticsApi, teamsApi } from '../../api/endpoints'
import type { CrossTeamRow } from '../../api/endpoints'
import { useAuthStore } from '../../stores/auth'
import Badge from '../../components/ui/Badge'
import EmptyState from '../../components/ui/EmptyState'
import LoadingState from '../../components/ui/LoadingState'

// ---------------------------------------------------------------------------
// Metric tooltips — explain what each data point means
// ---------------------------------------------------------------------------

const METRIC_TIPS: Record<string, string> = {
  // Call volume
  total: 'Total number of calls handled by this team in the selected period.',
  inbound: 'Calls received from customers or leads.',
  outbound: 'Calls initiated by agents to customers or leads.',
  connected: 'Calls where the agent and caller had a conversation (call was answered).',
  missed: 'Inbound calls that rang but were not answered by any agent.',
  abandoned: 'Calls where the caller hung up before being connected to an agent.',
  // Performance
  avg_handle_time: 'Average duration of connected calls. Lower is more efficient for support; longer may indicate thorough sales conversations.',
  fcr_rate: 'First Call Resolution — percentage of calls resolved on the first attempt without transfer or callback. Higher is better.',
  connect_rate: 'Percentage of total calls that were answered and connected. Target: 85%+ for support, 15-25% for outbound sales.',
  // Quality
  avg_quality: 'Average post-call quality rating (1-5 stars) given by agents. Target: 4.0+.',
  total_rated: 'Number of calls that received a quality rating from the agent.',
  outcome_dist: 'How calls ended — Resolved (issue fixed), Escalated (sent to senior), Transferred (moved to another team), Voicemail (left message), Abandoned (caller hung up).',
  // Routing
  avg_queue_wait: 'Average time callers waited in queue before being connected to an agent. Target: under 60 seconds.',
  avg_agents_tried: 'Average number of agents the system tried before one answered. 1.0 means first agent always picks up.',
  strategy_breakdown: 'Which routing strategy was used for calls — Round Robin (fair distribution), Least Busy (lowest load), Skill Based (expertise match).',
  // Capacity
  online_members: 'Agents currently available to take calls vs total team size.',
  avg_active_calls: 'Average number of simultaneous calls per agent right now.',
  utilization: 'Percentage of team members currently online and available.',
  // Trends
  trends: 'Daily call volume (bars) and average call duration (orange line) over the selected period. Helps spot busy days and capacity gaps.',
  // Agent
  vs_handle_time: 'How this agent\'s average call duration compares to the team average. Below 100% = faster than average.',
  vs_quality: 'How this agent\'s quality score compares to the team average. Above 100% = better than average.',
  vs_connect: 'How this agent\'s connect rate compares to the team average. Above 100% = connects more calls.',
  pickup_rate: 'Percentage of routed calls this agent answered (vs missed or declined).',
  avg_ring_time: 'Average time from when the agent\'s phone rings to when they pick up.',
}

/** Small "?" icon with hover tooltip */
function Tip({ id }: { id: string }) {
  const text = METRIC_TIPS[id]
  if (!text) return null
  return (
    <span className="relative group inline-flex ml-1" tabIndex={0} role="button" aria-label="More info">
      <HelpCircle size={13} className="text-gray-300 hover:text-gray-500 cursor-help transition-colors" />
      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2.5 py-1.5 bg-gray-900 text-white text-[11px] leading-tight rounded-lg shadow-lg w-56 text-center opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto group-focus-within:opacity-100 group-focus-within:pointer-events-auto transition-opacity z-50">
        {text}
      </span>
    </span>
  )
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PERIODS = [
  { value: '7d', label: '7 days' },
  { value: '14d', label: '14 days' },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
]

type SortKey = 'total_calls' | 'avg_handle_time_seconds' | 'avg_quality' | 'connect_rate' | 'missed_rate'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtDuration(seconds: number): string {
  if (seconds == null || isNaN(seconds)) return '--'
  const m = Math.floor(seconds / 60)
  const s = Math.round(seconds % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

function fmtPct(value: number): string {
  if (value == null || isNaN(value)) return '--'
  // Values are already percentages (e.g., 85.2 means 85.2%)
  return `${value.toFixed(1)}%`
}

function fmtSeconds(seconds: number | null): string {
  if (seconds == null || isNaN(seconds)) return '--'
  return `${seconds.toFixed(1)}s`
}

function rateColor(rate: number, thresholds: [number, number] = [85, 70]): string {
  if (rate == null || isNaN(rate)) return 'text-gray-400'
  if (rate >= thresholds[0]) return 'text-green-600'
  if (rate >= thresholds[1]) return 'text-amber-600'
  return 'text-red-600'
}

function qualityColor(score: number): string {
  if (score == null || isNaN(score)) return 'text-gray-400'
  if (score >= 4.0) return 'text-green-600'
  if (score >= 3.0) return 'text-amber-600'
  return 'text-red-600'
}

function formatLabel(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

// ---------------------------------------------------------------------------
// Star display
// ---------------------------------------------------------------------------

function Stars({ score, max = 5 }: { score: number; max?: number }) {
  if (score == null || isNaN(score)) return <span className="text-xs text-gray-400">--</span>
  const filled = Math.round(score)
  return (
    <span className="inline-flex items-center gap-0.5">
      {Array.from({ length: max }).map((_, i) => (
        <Star
          key={i}
          size={14}
          className={i < filled ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}
        />
      ))}
      <span className="ml-1 text-sm font-medium text-gray-700">{score.toFixed(1)}</span>
    </span>
  )
}

// ---------------------------------------------------------------------------
// Delta badge ("+5.7% above team avg" / "-12.3% below")
// ---------------------------------------------------------------------------

function DeltaBadge({ delta, label }: { delta: number; label: string }) {
  if (delta == null || isNaN(delta)) return null
  const positive = delta >= 0
  const Icon = positive ? TrendingUp : TrendingDown
  const color = positive ? 'text-green-700 bg-green-50' : 'text-red-700 bg-red-50'
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
      <Icon size={12} />
      {positive ? '+' : ''}{delta.toFixed(1)}% {label}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function TeamAnalyticsPage() {
  const user = useAuthStore((s) => s.user)
  const role = user?.role || ''

  // Only admin and manager can access
  if (!['admin', 'manager'].includes(role)) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Shield size={40} className="text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">You do not have permission to view team analytics.</p>
        </div>
      </div>
    )
  }

  return <TeamAnalyticsContent />
}

function TeamAnalyticsContent() {
  const [filters, setFilter] = useUrlFilters({ period: '7d' })
  const period = filters.period
  const setPeriod = (v: string) => setFilter('period', v)
  const [expandedTeamId, setExpandedTeamId] = useState<number | null>(null)
  const [sortKey, setSortKey] = useState<SortKey>('total_calls')
  const [sortAsc, setSortAsc] = useState(false)

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['teams-analytics-cross', period],
    queryFn: () => teamsAnalyticsApi.crossTeam(period),
  })

  const crossTeamData = data?.data
  const teams = crossTeamData?.teams || []

  // Sort teams
  const sortedTeams = [...teams].sort((a, b) => {
    const av = a[sortKey]
    const bv = b[sortKey]
    return sortAsc ? (av as number) - (bv as number) : (bv as number) - (av as number)
  })

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortAsc(!sortAsc)
    } else {
      setSortKey(key)
      setSortAsc(false)
    }
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Team Analytics</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Compare team performance and drill into individual metrics
          </p>
        </div>
        <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg p-1 overflow-x-auto scrollbar-hide">
          {PERIODS.map((p) => (
            <button
              key={p.value}
              onClick={() => setPeriod(p.value)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors whitespace-nowrap flex-shrink-0 ${
                period === p.value
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Loading */}
      {isLoading && (
        <LoadingState message="Loading team analytics..." />
      )}

      {/* Error */}
      {isError && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
          Could not load analytics data. {(error as Error)?.message || 'Please try again.'}
        </div>
      )}

      {/* Empty */}
      {!isLoading && !isError && teams.length === 0 && (
        <EmptyState
          icon={BarChart3}
          title="No analytics data yet"
          description="Analytics will appear here once teams have handled calls. Make sure teams are set up and calls are being routed."
        />
      )}

      {/* Cross-team comparison */}
      {!isLoading && !isError && teams.length > 0 && (
        <>
          {/* Mobile cards */}
          <div className="md:hidden space-y-3 mb-6">
            <h2 className="text-sm font-semibold text-gray-900 mb-2">Cross-Team Comparison</h2>
            {sortedTeams.map((team) => {
              const TYPE_BADGE_VARIANT: Record<string, 'info' | 'success' | 'purple' | 'warning'> = {
                sales: 'info', support: 'success', accounts: 'purple', custom: 'warning',
              }
              return (
                <div key={team.team_id} className="bg-white rounded-xl border border-gray-200 p-4">
                  <button
                    className="w-full text-left"
                    onClick={() => setExpandedTeamId(expandedTeamId === team.team_id ? null : team.team_id)}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        {expandedTeamId === team.team_id
                          ? <ChevronDown size={14} className="text-gray-400" />
                          : <ChevronRight size={14} className="text-gray-400" />}
                        <span className="font-semibold text-gray-900">{team.team_name}</span>
                      </div>
                      <Badge variant={TYPE_BADGE_VARIANT[team.team_type] || 'default'}>
                        {formatLabel(team.team_type)}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-[11px] text-gray-500 uppercase tracking-wider">Total Calls</p>
                        <p className="text-sm font-semibold text-gray-900">{team.total_calls}</p>
                      </div>
                      <div>
                        <p className="text-[11px] text-gray-500 uppercase tracking-wider">Avg Handle Time</p>
                        <p className="text-sm font-medium text-gray-700">{fmtDuration(team.avg_handle_time_seconds)}</p>
                      </div>
                      <div>
                        <p className="text-[11px] text-gray-500 uppercase tracking-wider">Quality</p>
                        <Stars score={team.avg_quality} />
                      </div>
                      <div>
                        <p className="text-[11px] text-gray-500 uppercase tracking-wider">Connect Rate</p>
                        <p className={`text-sm font-medium ${rateColor(team.connect_rate)}`}>{fmtPct(team.connect_rate)}</p>
                      </div>
                      <div>
                        <p className="text-[11px] text-gray-500 uppercase tracking-wider">Missed Rate</p>
                        <p className={`text-sm font-medium ${rateColor(100 - team.missed_rate)}`}>{fmtPct(team.missed_rate)}</p>
                      </div>
                      <div>
                        <p className="text-[11px] text-gray-500 uppercase tracking-wider">Capacity</p>
                        <p className="text-sm font-medium text-gray-700">
                          <span className="inline-flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full bg-green-500" />
                            {team.online_members} / {team.total_members}
                          </span>
                        </p>
                      </div>
                    </div>
                    {team.outcomes && Object.keys(team.outcomes).length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1.5">Call Outcomes</p>
                        <div className="flex gap-3">
                          {[
                            { key: 'interacted', label: 'Int', color: 'text-purple-600' },
                            { key: 'connected', label: 'Con', color: 'text-green-600' },
                            { key: 'answered', label: 'Ans', color: 'text-blue-600' },
                            { key: 'no_answer', label: 'N/A', color: 'text-gray-500' },
                          ].map(({ key, label, color }) => (
                            <span key={key} className={`text-xs ${color}`}>
                              <span className="font-semibold">{team.outcomes[key] || 0}</span> {label}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </button>
                  {expandedTeamId === team.team_id && (
                    <div className="mt-3 -mx-4 -mb-4">
                      <TeamDetailPanel teamId={team.team_id} period={period} />
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-900">Cross-Team Comparison</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider">Team</th>
                    <th className="text-center px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <SortHeader label="Total Calls" sortKey="total_calls" currentKey={sortKey} asc={sortAsc} onSort={handleSort} />
                    <SortHeader label="Avg Handle Time" sortKey="avg_handle_time_seconds" currentKey={sortKey} asc={sortAsc} onSort={handleSort} />
                    <SortHeader label="Quality" sortKey="avg_quality" currentKey={sortKey} asc={sortAsc} onSort={handleSort} />
                    <SortHeader label="Connect Rate" sortKey="connect_rate" currentKey={sortKey} asc={sortAsc} onSort={handleSort} />
                    <SortHeader label="Missed Rate" sortKey="missed_rate" currentKey={sortKey} asc={sortAsc} onSort={handleSort} />
                    <th className="text-center px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider">Outcomes</th>
                    <th className="text-center px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider">Capacity</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sortedTeams.map((team) => (
                    <CrossTeamRowItem
                      key={team.team_id}
                      team={team}
                      isExpanded={expandedTeamId === team.team_id}
                      onToggle={() =>
                        setExpandedTeamId(expandedTeamId === team.team_id ? null : team.team_id)
                      }
                      period={period}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Sortable header
// ---------------------------------------------------------------------------

function SortHeader({
  label,
  sortKey,
  currentKey,
  asc,
  onSort,
}: {
  label: string
  sortKey: SortKey
  currentKey: SortKey
  asc: boolean
  onSort: (key: SortKey) => void
}) {
  const isActive = sortKey === currentKey
  return (
    <th
      className="text-center px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none"
      onClick={() => onSort(sortKey)}
    >
      <span className="inline-flex items-center justify-center gap-1">
        {label}
        <ArrowUpDown size={12} className={isActive ? 'text-indigo-600' : 'text-gray-300'} />
        {isActive && (
          <span className="text-indigo-600 text-[10px]">{asc ? 'asc' : 'desc'}</span>
        )}
      </span>
    </th>
  )
}

// ---------------------------------------------------------------------------
// Cross-team row + expandable detail
// ---------------------------------------------------------------------------

function CrossTeamRowItem({
  team,
  isExpanded,
  onToggle,
  period,
}: {
  team: CrossTeamRow
  isExpanded: boolean
  onToggle: () => void
  period: string
}) {
  const TYPE_BADGE_VARIANT: Record<string, 'info' | 'success' | 'purple' | 'warning'> = {
    sales: 'info',
    support: 'success',
    accounts: 'purple',
    custom: 'warning',
  }

  return (
    <>
      <tr
        className="hover:bg-gray-50 transition-colors cursor-pointer"
        onClick={onToggle}
      >
        <td className="px-4 py-3">
          <span className="inline-flex items-center gap-2">
            {isExpanded ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
            <span className="font-medium text-gray-900">{team.team_name}</span>
          </span>
        </td>
        <td className="px-4 py-3 text-center">
          <Badge variant={TYPE_BADGE_VARIANT[team.team_type] || 'default'}>
            {formatLabel(team.team_type)}
          </Badge>
        </td>
        <td className="px-4 py-3 text-center font-medium text-gray-900">{team.total_calls}</td>
        <td className="px-4 py-3 text-center text-gray-700">{fmtDuration(team.avg_handle_time_seconds)}</td>
        <td className="px-4 py-3 text-center">
          <span className={qualityColor(team.avg_quality)}>
            <Stars score={team.avg_quality} />
          </span>
        </td>
        <td className="px-4 py-3 text-center">
          <span className={`font-medium ${rateColor(team.connect_rate)}`}>
            {fmtPct(team.connect_rate)}
          </span>
        </td>
        <td className="px-4 py-3 text-center">
          <span className={`font-medium ${rateColor(100 - team.missed_rate)}`}>
            {fmtPct(team.missed_rate)}
          </span>
        </td>
        <td className="px-4 py-3 text-center">
          <span className="inline-flex items-center gap-2 text-xs">
            {team.outcomes?.interacted ? <span className="text-purple-600 font-semibold">{team.outcomes.interacted}i</span> : null}
            {team.outcomes?.connected ? <span className="text-green-600 font-semibold">{team.outcomes.connected}c</span> : null}
            {team.outcomes?.answered ? <span className="text-blue-600 font-semibold">{team.outcomes.answered}a</span> : null}
            {team.outcomes?.no_answer ? <span className="text-gray-400">{team.outcomes.no_answer}n</span> : null}
            {!team.outcomes || Object.keys(team.outcomes).length === 0 ? <span className="text-gray-300">&mdash;</span> : null}
          </span>
        </td>
        <td className="px-4 py-3 text-center">
          <span className="inline-flex items-center justify-center gap-1.5 text-sm">
            <span className="w-2 h-2 rounded-full bg-green-500" />
            <span className="font-medium text-gray-900">{team.online_members}</span>
            <span className="text-gray-400">/</span>
            <span className="text-gray-500">{team.total_members}</span>
          </span>
        </td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={9} className="p-0">
            <TeamDetailPanel teamId={team.team_id} period={period} />
          </td>
        </tr>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Team detail panel (expanded inside the table)
// ---------------------------------------------------------------------------

function TeamDetailPanel({ teamId, period }: { teamId: number; period: string }) {
  const [selectedAgent, setSelectedAgent] = useState<{ teamId: number; userId: number } | null>(null)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['team-analytics', teamId, period],
    queryFn: () => teamsAnalyticsApi.teamAnalytics(teamId, period),
  })

  const analytics = data?.data

  if (isLoading) {
    return (
      <div className="bg-gray-50 border-t border-gray-100">
        <LoadingState message="Loading team details..." />
      </div>
    )
  }

  if (isError || !analytics) {
    return (
      <div className="px-6 py-8 bg-gray-50 border-t border-gray-100 text-center text-sm text-gray-500">
        Could not load team analytics.
      </div>
    )
  }

  const vol = analytics.call_volume
  const perf = analytics.performance
  const quality = analytics.quality
  const routing = analytics.routing
  const capacity = analytics.capacity
  const trends = analytics.trends || []

  return (
    <div className="bg-gray-50 border-t border-gray-100">
      <div className="px-6 py-5 space-y-6">
        {/* Call volume + outcomes */}
        <div>
          <h4 className="text-xs font-semibold text-indigo-600 uppercase tracking-wider mb-3">Business Performance</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <StatCard icon={Phone} label="Total Calls" value={vol.total} tip="total" />
            <StatCard icon={PhoneIncoming} label="Inbound" value={vol.inbound} tip="inbound" />
            <StatCard icon={PhoneOutgoing} label="Outbound" value={vol.outbound} tip="outbound" />
            <StatCard icon={Phone} label="Interacted" value={vol.outcomes?.interacted || 0} color="text-purple-600" />
            <StatCard icon={Phone} label="Connected" value={vol.outcomes?.connected || 0} color="text-green-600" />
            <StatCard icon={PhoneOff} label="No Answer" value={vol.outcomes?.no_answer || 0} color="text-gray-500" />
          </div>
          {/* Rates bar */}
          {vol.total > 0 && (
            <div className="mt-3 flex items-center gap-4 text-xs">
              <span className="text-gray-500">Interaction Rate: <span className="font-semibold text-purple-600">{fmtPct(vol.interaction_rate)}</span></span>
              <span className="text-gray-500">Reach Rate: <span className="font-semibold text-green-600">{fmtPct(vol.reach_rate)}</span></span>
            </div>
          )}
        </div>

        {/* Performance + Quality + Routing + Capacity */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Performance */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Performance</h4>
            <div className="space-y-3">
              <MetricRow label="Avg Handle Time" value={fmtDuration(perf.avg_handle_time_seconds)} tip="avg_handle_time" />
              <MetricRow label="FCR Rate" value={fmtPct(perf.first_call_resolution_rate)} tip="fcr_rate" />
              <MetricRow
                label="Connect Rate"
                value={fmtPct(perf.connect_rate)}
                valueClass={rateColor(perf.connect_rate)}
                tip="connect_rate"
              />
            </div>
          </div>

          {/* Quality */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Quality<Tip id="avg_quality" /></h4>
            <div className="mb-3">
              <Stars score={quality.avg_score} />
              <span className="text-xs text-gray-500 ml-2">({quality.total_rated} rated<Tip id="total_rated" />)</span>
            </div>
            {Object.keys(quality.outcome_distribution).length > 0 && (
              <OutcomeBar distribution={quality.outcome_distribution} />
            )}
          </div>

          {/* Routing */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Routing</h4>
            <div className="space-y-3">
              <MetricRow label="Avg Queue Wait" value={fmtSeconds(routing.avg_queue_wait_seconds)} tip="avg_queue_wait" />
              <MetricRow label="Avg Agents Tried" value={routing.avg_agents_tried != null ? routing.avg_agents_tried.toFixed(1) : '--'} tip="avg_agents_tried" />
              {Object.entries(routing.strategy_breakdown).map(([strat, count]) => (
                <MetricRow key={strat} label={formatLabel(strat)} value={String(count)} />
              ))}
            </div>
          </div>

          {/* Capacity */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Capacity</h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">Online / Total</span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-green-500" />
                  <span className="text-sm font-medium text-gray-900">{capacity.online_members}</span>
                  <span className="text-gray-400">/</span>
                  <span className="text-sm text-gray-500">{capacity.total_members}</span>
                </span>
              </div>
              <MetricRow label="Avg Active Calls" value={capacity.avg_active_calls != null ? capacity.avg_active_calls.toFixed(1) : '--'} tip="avg_active_calls" />
              {capacity.total_members > 0 && (
                <div>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-gray-500">Utilization</span>
                    <span className="text-gray-700 font-medium">
                      {capacity.total_members > 0 ? ((capacity.online_members / capacity.total_members) * 100).toFixed(0) : 0}%
                    </span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-indigo-500 rounded-full transition-all"
                      style={{ width: `${capacity.total_members > 0 ? (capacity.online_members / capacity.total_members) * 100 : 0}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Trends chart */}
        {trends.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Daily Trends<Tip id="trends" />
            </h4>
            <TrendsChart trends={trends} />
          </div>
        )}

        {/* Agent drill-down prompt */}
        <div>
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
            Agent Performance
          </h4>
          <AgentList teamId={teamId} selectedAgent={selectedAgent} onSelect={(a) => setSelectedAgent(a)} />
        </div>

        {/* Agent detail panel */}
        {selectedAgent && selectedAgent.teamId === teamId && (
          <AgentDetailPanel teamId={selectedAgent.teamId} userId={selectedAgent.userId} period={period} />
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Stat card
// ---------------------------------------------------------------------------

function StatCard({
  icon: Icon,
  label,
  value,
  color = 'text-gray-900',
  tip,
}: {
  icon: typeof Phone
  label: string
  value: number
  color?: string
  tip?: string
}) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-3 relative">
      {tip && (
        <span className="absolute top-2 right-2">
          <Tip id={tip} />
        </span>
      )}
      <div className="flex items-center gap-1.5 mb-2">
        <Icon size={14} className="text-gray-400" />
        <span className="text-xs text-gray-500">{label}</span>
      </div>
      <div className="text-center">
        <span className={`text-xl font-bold ${color}`}>{value}</span>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Metric row
// ---------------------------------------------------------------------------

function MetricRow({
  label,
  value,
  valueClass = 'text-gray-900',
  tip,
}: {
  label: string
  value: string
  valueClass?: string
  tip?: string
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-gray-500">{label}{tip && <Tip id={tip} />}</span>
      <span className={`text-sm font-medium ${valueClass}`}>{value}</span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Outcome distribution bar (horizontal stacked)
// ---------------------------------------------------------------------------

function OutcomeBar({ distribution }: { distribution: Record<string, number> }) {
  const total = Object.values(distribution).reduce((a, b) => a + b, 0)
  if (total === 0) return null

  const OUTCOME_COLORS: Record<string, string> = {
    positive: 'bg-green-500',
    interested: 'bg-green-400',
    follow_up: 'bg-blue-500',
    callback: 'bg-blue-400',
    not_interested: 'bg-amber-500',
    no_answer: 'bg-gray-400',
    negative: 'bg-red-500',
    voicemail: 'bg-purple-400',
  }

  return (
    <div>
      <div className="flex w-full h-3 rounded-full overflow-hidden">
        {Object.entries(distribution).map(([outcome, count]) => {
          const pct = (count / total) * 100
          if (pct < 1) return null
          return (
            <div
              key={outcome}
              className={`${OUTCOME_COLORS[outcome] || 'bg-gray-300'} transition-all`}
              style={{ width: `${pct}%` }}
              title={`${formatLabel(outcome)}: ${count} (${pct.toFixed(1)}%)`}
            />
          )
        })}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
        {Object.entries(distribution).map(([outcome, count]) => (
          <span key={outcome} className="inline-flex items-center gap-1 text-[11px] text-gray-500">
            <span className={`w-2 h-2 rounded-full ${OUTCOME_COLORS[outcome] || 'bg-gray-300'}`} />
            {formatLabel(outcome)} ({count})
          </span>
        ))}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Trends chart (SVG line chart with bars)
// ---------------------------------------------------------------------------

function TrendsChart({ trends }: { trends: { date: string; calls: number; avg_duration_seconds: number }[] }) {
  if (trends.length === 0) return null

  const chartWidth = 720
  const chartHeight = 180
  const padLeft = 40
  const padRight = 20
  const padTop = 10
  const padBottom = 30
  const innerW = chartWidth - padLeft - padRight
  const innerH = chartHeight - padTop - padBottom

  const maxCalls = Math.max(...trends.map((t) => t.calls), 1)
  const maxDuration = Math.max(...trends.map((t) => t.avg_duration_seconds ?? 0), 1)

  const barWidth = trends.length === 1 ? 32 : Math.max(8, Math.min(24, innerW / trends.length - 4))
  const barSpacing = trends.length <= 1 ? 0 : innerW / (trends.length - 1)

  // Bar positions — evenly spaced, centered when single point
  const bars = trends.map((t, i) => {
    const x = trends.length === 1
      ? padLeft + innerW / 2
      : padLeft + i * barSpacing
    const barH = Math.max(2, (t.calls / maxCalls) * innerH)
    return {
      x: x - barWidth / 2,
      y: padTop + innerH - barH,
      width: barWidth,
      height: barH,
      calls: t.calls,
      date: t.date,
    }
  })

  // Line path for avg duration
  const linePoints = trends.map((t, i) => {
    const x = trends.length === 1
      ? padLeft + innerW / 2
      : padLeft + i * barSpacing
    const dur = t.avg_duration_seconds ?? 0
    const y = padTop + innerH - (dur / maxDuration) * innerH
    return `${x},${y}`
  })
  const linePath = `M${linePoints.join(' L')}`

  // X labels (show max 10)
  const step = Math.max(1, Math.ceil(trends.length / 10))
  const xLabels = trends.filter((_, i) => i % step === 0 || i === trends.length - 1)

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center gap-4 mb-3">
        <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
          <span className="w-3 h-2 bg-indigo-200 rounded-sm" />
          Calls
        </span>
        <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
          <span className="w-3 h-0.5 bg-orange-500 rounded" />
          Avg Duration
        </span>
      </div>
      <div className="overflow-x-auto">
        <svg
          viewBox={`0 0 ${chartWidth} ${chartHeight}`}
          className="w-full max-w-[720px]"
          preserveAspectRatio="xMidYMid meet"
        >
          {/* Grid lines */}
          {[0.25, 0.5, 0.75, 1].map((pct) => (
            <line
              key={pct}
              x1={padLeft}
              y1={padTop + innerH * (1 - pct)}
              x2={chartWidth - padRight}
              y2={padTop + innerH * (1 - pct)}
              stroke="#e5e7eb"
              strokeWidth={0.5}
            />
          ))}

          {/* Y axis labels (calls) */}
          {[0, 0.5, 1].map((pct) => (
            <text
              key={pct}
              x={padLeft - 6}
              y={padTop + innerH * (1 - pct) + 3}
              textAnchor="end"
              className="fill-gray-400"
              fontSize={10}
            >
              {Math.round(maxCalls * pct)}
            </text>
          ))}

          {/* Bars */}
          {bars.map((b, i) => (
            <rect
              key={i}
              x={b.x}
              y={b.y}
              width={b.width}
              height={b.height}
              rx={2}
              className="fill-indigo-200 hover:fill-indigo-300 transition-colors"
            >
              <title>{b.date}: {b.calls} calls</title>
            </rect>
          ))}

          {/* Duration line */}
          <path d={linePath} fill="none" stroke="#f97316" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />

          {/* Duration dots */}
          {trends.map((t, i) => {
            const x = trends.length === 1
              ? padLeft + innerW / 2
              : padLeft + i * barSpacing
            const dur = t.avg_duration_seconds ?? 0
            const y = padTop + innerH - (dur / maxDuration) * innerH
            return (
              <circle key={i} cx={x} cy={y} r={3} className="fill-orange-500">
                <title>{t.date}: {fmtDuration(dur)} avg</title>
              </circle>
            )
          })}

          {/* X labels */}
          {xLabels.map((t) => {
            const idx = trends.indexOf(t)
            const x = trends.length === 1
              ? padLeft + innerW / 2
              : padLeft + idx * barSpacing
            // Short date label
            const d = new Date(t.date)
            const label = `${d.getDate()}/${d.getMonth() + 1}`
            return (
              <text
                key={idx}
                x={x}
                y={chartHeight - 6}
                textAnchor="middle"
                className="fill-gray-400"
                fontSize={10}
              >
                {label}
              </text>
            )
          })}
        </svg>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Agent list (fetch team detail to get members, show clickable rows)
// ---------------------------------------------------------------------------

function AgentList({
  teamId,
  selectedAgent,
  onSelect,
}: {
  teamId: number
  selectedAgent: { teamId: number; userId: number } | null
  onSelect: (a: { teamId: number; userId: number } | null) => void
}) {
  // Re-use the teams detail endpoint for members
  const { data, isLoading } = useQuery({
    queryKey: ['teams', teamId],
    queryFn: () => teamsApi.get(teamId),
  })

  const members = data?.data?.members || []

  if (isLoading) {
    return <div className="text-sm text-gray-400">Loading agents...</div>
  }

  if (members.length === 0) {
    return <div className="text-sm text-gray-500">No members in this team.</div>
  }

  return (
    <>
      {/* Mobile cards */}
      <div className="md:hidden space-y-2">
        {members.map((m: any) => {
          const isSelected = selectedAgent?.userId === m.user_id && selectedAgent?.teamId === teamId
          return (
            <button
              key={m.user_id}
              className={`w-full text-left bg-white rounded-xl border p-4 transition-colors ${
                isSelected ? 'border-indigo-300 bg-indigo-50' : 'border-gray-200 active:bg-gray-50'
              }`}
              onClick={() => onSelect(isSelected ? null : { teamId, userId: m.user_id })}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-indigo-600">{m.user_name}</span>
                <Badge variant={m.team_role === 'manager' ? 'purple' : m.team_role === 'lead' ? 'info' : 'default'}>
                  {formatLabel(m.team_role)}
                </Badge>
              </div>
              <div className="flex items-center gap-4">
                <span className="inline-flex items-center gap-1.5 text-xs text-gray-600">
                  <span className={`w-2 h-2 rounded-full ${
                    m.availability === 'online' ? 'bg-green-500'
                      : m.availability === 'on_call' ? 'bg-orange-500'
                      : m.availability === 'break' ? 'bg-yellow-400'
                      : 'bg-gray-400'
                  }`} />
                  {formatLabel(m.availability)}
                </span>
                <span className="text-xs text-gray-500">
                  Active calls: <span className={`font-medium ${m.active_call_count > 0 ? 'text-indigo-600' : 'text-gray-700'}`}>{m.active_call_count}</span>
                </span>
              </div>
            </button>
          )
        })}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider">Agent</th>
              <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider">Team Role</th>
              <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider">Availability</th>
              <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider">Active Calls</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {members.map((m: any) => {
              const isSelected = selectedAgent?.userId === m.user_id && selectedAgent?.teamId === teamId
              return (
                <tr
                  key={m.user_id}
                  className={`cursor-pointer transition-colors ${
                    isSelected ? 'bg-indigo-50' : 'hover:bg-gray-50'
                  }`}
                  onClick={() =>
                    onSelect(isSelected ? null : { teamId, userId: m.user_id })
                  }
                >
                  <td className="px-4 py-3">
                    <span className="font-medium text-indigo-600 hover:text-indigo-800">{m.user_name}</span>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant={m.team_role === 'manager' ? 'purple' : m.team_role === 'lead' ? 'info' : 'default'}>
                      {formatLabel(m.team_role)}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1.5 text-xs">
                      <span className={`w-2.5 h-2.5 rounded-full ${
                        m.availability === 'online' ? 'bg-green-500'
                          : m.availability === 'on_call' ? 'bg-orange-500'
                          : m.availability === 'break' ? 'bg-yellow-400'
                          : 'bg-gray-400'
                      }`} />
                      {formatLabel(m.availability)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`font-medium ${m.active_call_count > 0 ? 'text-indigo-600' : 'text-gray-500'}`}>
                      {m.active_call_count}
                    </span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// Agent detail panel
// ---------------------------------------------------------------------------

function AgentDetailPanel({
  teamId,
  userId,
  period,
}: {
  teamId: number
  userId: number
  period: string
}) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['agent-analytics', teamId, userId, period],
    queryFn: () => teamsAnalyticsApi.agentAnalytics(teamId, userId, period),
  })

  const agent = data?.data

  if (isLoading) {
    return <LoadingState message="Loading agent analytics..." />
  }

  if (isError || !agent) {
    return (
      <div className="text-center py-6 text-sm text-gray-500">
        Could not load agent analytics.
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg border border-indigo-200 p-5">
      {/* Agent header */}
      <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-100">
        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
          <span className="text-sm font-semibold text-indigo-700">
            {agent.user_name.charAt(0).toUpperCase()}
          </span>
        </div>
        <div>
          <h4 className="text-sm font-semibold text-gray-900">{agent.user_name}</h4>
          <Badge variant={agent.team_role === 'manager' ? 'purple' : agent.team_role === 'lead' ? 'info' : 'default'}>
            {formatLabel(agent.team_role)}
          </Badge>
        </div>
      </div>

      {/* ── BUSINESS PERFORMANCE (from call_logs) ── */}
      <div className="mb-5">
        <h4 className="text-xs font-semibold text-indigo-600 uppercase tracking-wider mb-3">Business Performance</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Activity */}
          <div>
            <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Activity</h5>
            <div className="space-y-2">
              <MetricRow label="Total Calls" value={String((agent.performance as any)?.total_calls ?? 0)} />
              <MetricRow label="Outbound" value={String((agent.performance as any)?.outbound ?? 0)} />
              <MetricRow label="Inbound" value={String((agent.performance as any)?.inbound ?? 0)} />
              <MetricRow label="Avg Duration" value={(agent.performance as any)?.avg_duration_minutes != null ? `${(agent.performance as any).avg_duration_minutes} min` : '--'} />
            </div>
          </div>

          {/* Outcome Tiers */}
          <div>
            <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Outcome Tiers</h5>
            {(() => {
              const outcomes = (agent.performance as any)?.outcomes || {}
              const total = (agent.performance as any)?.total_calls || 0
              if (Object.keys(outcomes).length === 0) return <p className="text-xs text-gray-400">No data yet</p>
              return (
                <div className="space-y-2">
                  {[
                    { key: 'interacted', label: 'Interacted (>60s)', color: 'text-purple-600' },
                    { key: 'connected', label: 'Connected (11-60s)', color: 'text-green-600' },
                    { key: 'answered', label: 'Answered (1-10s)', color: 'text-blue-600' },
                    { key: 'no_answer', label: 'No Answer', color: 'text-gray-500' },
                    { key: 'busy', label: 'Busy', color: 'text-amber-600' },
                  ].map(({ key, label, color }) => {
                    const count = outcomes[key] || 0
                    if (count === 0) return null
                    const pct = total > 0 ? Math.round((count / total) * 100) : 0
                    return (
                      <div key={key} className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">{label}</span>
                        <span className={`text-sm font-medium ${color}`}>{count} <span className="text-xs text-gray-400">({pct}%)</span></span>
                      </div>
                    )
                  })}
                </div>
              )
            })()}
          </div>

          {/* Rates + Vs Team */}
          <div>
            <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Effectiveness</h5>
            <div className="space-y-2">
              <MetricRow label="Interaction Rate" value={fmtPct((agent.performance as any)?.interaction_rate)} valueClass="text-purple-600" />
              <MetricRow label="Reach Rate" value={fmtPct((agent.performance as any)?.reach_rate)} valueClass="text-green-600" />
            </div>
            <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 mt-4">vs Team Average</h5>
            <div className="flex flex-wrap gap-2">
              {agent.vs_team.interaction_rate_delta != null && <DeltaBadge delta={agent.vs_team.interaction_rate_delta} label="interaction" />}
              {agent.vs_team.reach_rate_delta != null && <DeltaBadge delta={agent.vs_team.reach_rate_delta} label="reach" />}
              {agent.vs_team.quality_delta != null && <DeltaBadge delta={agent.vs_team.quality_delta} label="quality" />}
            </div>
          </div>
        </div>
      </div>

      {/* ── VOIP OPERATIONS (from voip_calls) ── */}
      <div className="pt-4 border-t border-gray-200">
        <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">VoIP Operations</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Quality */}
          <div>
            <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Quality</h5>
            <div className="space-y-2">
              <div>
                <Stars score={agent.quality.avg_score} />
                <span className="text-xs text-gray-500 ml-2">({agent.quality.total_rated} rated)</span>
              </div>
              <MetricRow label="Avg Handle Time" value={fmtDuration((agent.quality as any)?.avg_handle_time_seconds)} />
            </div>
          </div>

          {/* Responsiveness */}
          <div>
            <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Responsiveness</h5>
            <div className="space-y-2">
              <MetricRow label="Avg Ring Time" value={fmtSeconds(agent.responsiveness.avg_ring_time_seconds)} />
              <MetricRow label="Pickup Rate" value={fmtPct(agent.responsiveness.pickup_rate)} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
